import { useEffect, useRef } from "react";

import { useToast } from "../components/ToastHost";
import { buildSourceMap, STANDARD_BENCHMARK_SOURCES } from "./flagMap.mjs";
import type { BenchmarkSourceRow } from "./flagMap.d.mts";
import { createProbeBackoff } from "./probeBackoff.mjs";
import { formatRedFlagToast } from "./redFlagToast.mjs";
import { getSupabase } from "./supabase";
import { useTenantId } from "./tenant";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-167: poll the `flag` table for new RED rows and surface a toast
 * when the count grows since dashboard mount. Conservative because the
 * toast is meant to catch *new* critical alerts, not re-fire every
 * page load for existing ones.
 *
 * Strategy:
 * - On mount, fetch current count of RED flags. Store as baseline.
 * - Every 60s, re-fetch count.
 * - If count > baseline, push a toast with the delta. MER-688: only a
 *   staff session gets the `View Findings` action and the body that names
 *   Findings (a Meridian-internal surface); a client gets copy it can act
 *   on from the marked tiles and no action. Update baseline to the new
 *   count so we don't re-fire on the next tick.
 *
 * MER-716: only a RED flag graded against a standard source (the
 * customer's own target, a cited industry reference) counts. A RED flag
 * graded against the customer's own history is a trend, not a critical
 * alert, so it never raises this toast.
 *
 * Deferred: real-time via Supabase channels. The poll is good enough
 * for the 4h scheduler cadence and avoids the WebSocket auth dance.
 */

const POLL_INTERVAL_MS = 60_000;
const PROBE_TIMEOUT_MS = 8000;

/**
 * MER-503: failures are LOUD in console (a silently-503ing poll is
 * indistinguishable from healthy-quiet, the MER-464 lesson), bounded
 * by an abort timeout so a hung request can't block page idle, and
 * the caller backs off on consecutive failures instead of hammering a
 * degraded backend every 60s. Returns null on failure; the caller's
 * backoff distinguishes that from a healthy zero.
 */
async function fetchRedFlagCount(tenantId: string): Promise<number | null> {
  try {
    const supabase = getSupabase();
    // MER-716: the benchmarks a RED flag may be judged against. None means no
    // flag on this tenant is a standard judgment, which is a healthy zero.
    const standard = await scopeTenant(
      supabase.from("benchmark").select("benchmark_id,benchmark_source"),
      tenantId,
    )
      .in("benchmark_source", [...STANDARD_BENCHMARK_SOURCES])
      .abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS));
    if (standard.error) {
      console.warn(`flagStream poll failed: ${standard.error.message}`);
      return null;
    }
    const standardIds = [...buildSourceMap((standard.data ?? []) as BenchmarkSourceRow[]).keys()];
    if (standardIds.length === 0) return 0;
    const { count, error } = await scopeTenant(
      supabase.from("flag").select("*", { count: "exact", head: true }),
      tenantId,
    )
      .eq("flag_level", "RED")
      .in("benchmark_id", standardIds)
      .abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS));
    if (error) {
      console.warn(`flagStream poll failed: ${error.message}`);
      return null;
    }
    return count ?? 0;
  } catch (err) {
    console.warn(`flagStream poll failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

export function useFlagStream(slug: string | undefined, opts: { isStaff: boolean }): void {
  const { push } = useToast();
  const staff = opts.isStaff === true;
  const baselineRef = useRef<number | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    if (!slug || !tenantId) return;
    const boundTenantId = tenantId;
    let cancelled = false;
    // MER-503: consecutive failures double the ticks skipped between
    // attempts (cap 8 -> one attempt per ~8 minutes while degraded).
    const backoff = createProbeBackoff();

    async function check() {
      if (!backoff.shouldAttempt()) return;
      const current = await fetchRedFlagCount(boundTenantId);
      if (cancelled) return;
      if (current === null) {
        backoff.onFailure();
        return;
      }
      backoff.onSuccess();
      const baseline = baselineRef.current;
      if (baseline === null) {
        baselineRef.current = current;
        return;
      }
      if (current > baseline) {
        const delta = current - baseline;
        const copy = formatRedFlagToast(delta, { isStaff: staff });
        push({
          type: "error",
          title: copy.title,
          body: copy.body,
          sticky: true,
          dedupeKey: `red-flag-${current}`,
          action:
            copy.actionLabel === null
              ? undefined
              : {
                  label: copy.actionLabel,
                  onClick: () => {
                    window.location.assign(`/${slug}/Findings`);
                  },
                },
        });
        baselineRef.current = current;
      }
    }

    void check();
    const handle = window.setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [slug, push, tenantId, staff]);
}
